import { pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import ExportIcon from "src/core/assets/images/icons/export.svg?react";
import { FolderPdfDocument } from "src/core/components/pdf/FolderPdfDocument";
import { Button } from "src/core/components/ui/button";
import {
  useCartographyRisks,
  useFolder,
  useFolderCompanyName,
} from "src/core/contexts/FolderContext";
import { useFolderGraphExport } from "src/core/contexts/FolderGraphExportContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { useUser } from "src/core/contexts/UserContext";
import { downloadPdfBlob } from "src/core/utils/pdf";

type ExportPdfFolderActionProps = {
  variant?: "linkDestructive" | "linkWhite" | "default";
  icon?: React.ReactNode;
  label?: string;
};

export const ExportPdfFolderAction = ({
  variant = "linkDestructive",
  icon = <ExportIcon />,
  label = undefined,
}: ExportPdfFolderActionProps) => {
  const { t } = useTranslation();
  if (!label) {
    label = t("folder.export.download");
  }
  const { folderFile } = useFolder();
  const { user: currentUser } = useUser();
  const companyName = useFolderCompanyName();

  const {
    missionTypeWithRiskOptions,
    activityWithRiskOptions,
    countryWithRiskOptions,
    legalTypesWithRiskOptions,
    legalStatusOptions,
    legalStatusPPHOptions,
  } = useRiskOptions();
  const folderRisks = useCartographyRisks();
  const { getGraphPng } = useFolderGraphExport();

  const handleExportPdfFolder = async () => {
    if (!folderFile.folderFile?.id || !currentUser) {
      return;
    }

    try {
      if (!folderFile.folderFile) {
        console.error("[ExportPdfFolderAction] No folder file data retrieved");
        return;
      }

      const graphImageDataUrl = await getGraphPng();

      const blob = await pdf(
        <FolderPdfDocument
          currentUser={currentUser}
          folderFile={folderFile.folderFile}
          folderRisks={folderRisks}
          graphImageDataUrl={graphImageDataUrl}
          missionTypeWithRiskOptions={missionTypeWithRiskOptions}
          activityWithRiskOptions={activityWithRiskOptions}
          countryWithRiskOptions={countryWithRiskOptions}
          legalTypesWithRiskOptions={legalTypesWithRiskOptions}
          legalStatusOptions={legalStatusOptions}
          legalStatusPPHOptions={legalStatusPPHOptions}
        />
      ).toBlob();

      const fileName = companyName ? `${companyName}.pdf` : "dossier.pdf";
      downloadPdfBlob(blob, fileName);
    } catch (error) {
      console.error("[ExportPdfFolderAction] Error : ", error);
    }
  };

  return (
    <Button
      variant={variant}
      onClick={() => {
        handleExportPdfFolder();
      }}
      className="w-full"
    >
      {icon}
      {label}
    </Button>
  );
};
